import React, { useEffect, useRef, useState } from "react";
import {
  DeleteOutlined,
  EditOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type {
  InputRef,
  PopconfirmProps,
  TableColumnsType,
  TableColumnType,
} from "antd";
import { Button, Input, message, Popconfirm, Space, Table } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { deleteUser, fetchUsers } from "../../store/slices/userSlice";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import EditUserDrawer from "../EditUserForm/EditUserDrawer";

interface DataType {
  id: number;
  email: string;
  prenom: string;
  nom: string;
  role: "admin" | "controleur" | "visiteur";
}

type DataIndex = keyof DataType;

const UsersTable: React.FC = () => {
  const [editUserID, setEditUserID] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const showDrawer = (id: number) => {
    setOpen(true);
    setEditUserID(id);
  };

  const onClose = () => {
    setOpen(false);
  };

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);
  const dispatch = useDispatch<AppDispatch>();
  const { users: data, loading } = useSelector(
    (state: RootState) => state.user
  );
  const isLoading: boolean =
    loading !== "idle" && loading !== "succeeded" && loading !== "failed";

  const handleEdit = (id: number) => {
    console.log("record id is " + id);
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const confirm = async (id: number) => {
    try {
      // Call deleteUser and wait for the Promise to resolve
      await dispatch(deleteUser(id)); // Correct use of unwrap
      toast.success("Utilisateur supprimé avec succès");

      // Fetch the updated user list
      await dispatch(fetchUsers());
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Une erreur est survenue.");
    }
  };

  const cancel: PopconfirmProps["onCancel"] = (e) => {};

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: TableColumnsType<DataType> = [
    {
      title: "Nom",
      dataIndex: "nom",
      key: "nom",
      //fixed: "left",
      ...getColumnSearchProps("nom"),
    },
    {
      title: "Prenom",
      dataIndex: "prenom",
      key: "prenom",
      responsive: ["md"],
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      responsive: ["md"],
    },
    {
      title: "Telephone",
      dataIndex: "telephone",
      key: "telephone",
      responsive: ["md"],
    },
    { title: "Role", dataIndex: "role", key: "role", responsive: ["sm"] },
    {
      title: "Opérations",
      key: "operation",
      fixed: "right",
      render: (_, record) => (
        <>
          <Button
            style={{ marginRight: "10px" }}
            variant="solid"
            color="primary"
            onClick={() => showDrawer(record.id)}
          >
            <EditOutlined />
          </Button>

          <Popconfirm
            title="Supprimer un utilisateur"
            description="Etes-vous sûr ?"
            onConfirm={() => confirm(record.id)}
            onCancel={cancel}
            okText="Oui"
            cancelText="Non"
            icon={<QuestionCircleOutlined style={{ color: "red" }} />}
          >
            <Button variant="solid" color="danger">
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  return (
    <>
      <Table<DataType>
        size="small"
        columns={columns}
        dataSource={data}
        loading={isLoading}
        scroll={{ x: "true" }}
        style={{ padding: "0 10px" }}
        rowKey={(record) => record.id}
      />
      <EditUserDrawer id={editUserID} onClose={onClose} open={open} />
    </>
  );
};

export default UsersTable;
